import { rem } from 'polished';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'recompose';
import styled, { css } from 'styled-components';
import { Button, ButtonSize } from '@unmind/design-system';
import { small } from 'utils';
import RoutePath from '../../../../App/RoutePath';
import { tracking } from '../../../../App/Tracking';
import {
  withSeriesTrackingIds,
  withSeriesTrackingIdsChildProps,
} from '../withSeriesTrackingIds';
import {
  withSendSeriesEndOfDayReminder,
  WithSendSeriesEndOfDayReminderChildProps,
} from '../withSendSeriesEndOfDayReminder';
import { BodyText } from '../../../../Shared/Typography';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled(BodyText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize18,
    theme.typography.fontSizes.fontSize20,
    theme.typography.fontSizes.fontSize22,
    theme.typography.fontSizes.fontSize24,
  ],
  weight: theme.typography.fontWeights.medium,
}))`
  margin-top: ${rem(32)};
`;

const Message = styled(BodyText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize18,
    theme.typography.fontSizes.fontSize20,
    theme.typography.fontSizes.fontSize22,
    theme.typography.fontSizes.fontSize24,
  ],
}))`
  margin-top: ${rem(10)};
  margin-bottom: ${rem(36)};
`;

const ButtonsContainer = styled.div`
  margin-bottom: ${rem(36)};
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;

  ${small(css`
    margin-bottom: ${rem(12)};
  `)}
`;

const PrimaryProgressButton = styled(Button)`
  ${small(css`
    min-width: ${rem(190)};
  `)};
`;

const SecondaryProgressButton = styled(Button)`
  ${small(css`
    min-width: ${rem(190)};
  `)}
`;

const NextSessionButton = styled(Button)`
  margin-left: ${rem(20)};
  ${small(css`
    min-width: ${rem(190)};
  `)}
`;

const RemindMeButton = styled(Button)`
  margin-left: ${rem(20)};
  ${small(css`
    min-width: ${rem(190)};
  `)}
`;

interface OuterCongratulationsDayProps {
  slug: string;
  sessionNumber: number;
  lastSession: boolean;
  hasReminder: boolean;
}

export interface CongratulationsDayProps
  extends
    RouteComponentProps,
    OuterCongratulationsDayProps,
    WithSendSeriesEndOfDayReminderChildProps,
    withSeriesTrackingIdsChildProps {}

export const CongratulationsDay = (props: CongratulationsDayProps) => {
  const { t: translate } = useTranslation('courses');
  const { sessionNumber, slug, seriesId, hasReminder } = props;

  const labels = {
    viewProgress: translate('course_day.complete.view_progress_button.label'),
    nextSession: translate('course_day.cta_button.continue_variant.text'),
    sendReminder: translate('course_day.complete.send_reminder_button.label'),
    title: translate('course_day.complete.title', {
      session_number: sessionNumber,
    }),
    subtitle: translate('course_day.complete.subtitle'),
  };

  const dayId = props.id;

  const navigateToProgress = (): void => {
    tracking.track('series-end-of-day-view-progress-clicked', {
      seriesId,
      dayId,
    });

    props.history.push(`${RoutePath.Series}/${slug}/#progress`, {
      initialDay: sessionNumber,
    });
  };

  const navigateToNextDay = (): void => {
    props.history.push(`${RoutePath.Series}/${slug}/day/${sessionNumber + 1}`, {
      initialDay: sessionNumber + 1,
    });
  };

  const sendReminder = async () => {
    const { sendSeriesEndOfDayReminder } = props;
    const response = await sendSeriesEndOfDayReminder({
      seriesLink: `${RoutePath.Series}/${slug}/#progress`,
    });

    tracking.track('series-end-of-day-reminder-clicked', { seriesId, dayId });

    props.history.push({
      pathname: `${RoutePath.Series}/${slug}/reminder`,
      state: { seriesId, error: !response.success },
    });
  };
  const buttons = [
    hasReminder ? (
      <SecondaryProgressButton
        key="secondary-progress"
        data-testid="congratulations-day-cta"
        onClick={navigateToProgress}
        label={labels.viewProgress}
        size={ButtonSize.medium}
      />
    ) : (
      <PrimaryProgressButton
        variant="primary"
        key="primary-progress"
        data-testid="congratulations-day-cta"
        onClick={navigateToProgress}
        label={labels.viewProgress}
        size={ButtonSize.medium}
      />
    ),
    !props.lastSession && (
      <NextSessionButton
        key="next-session"
        data-testid="next-session-cta"
        onClick={navigateToNextDay}
        label={labels.nextSession}
        size={ButtonSize.medium}
      />
    ),
    hasReminder && (
      <RemindMeButton
        variant="primary"
        key={'remind-me'}
        data-testid="remind-me-day-cta"
        onClick={sendReminder}
        label={labels.sendReminder}
        size={ButtonSize.medium}
      />
    ),
  ].filter(Boolean);

  return (
    <Container>
      <Header>{labels.title}</Header>
      <Message>{labels.subtitle}</Message>
      <ButtonsContainer>
        {buttons.map((button, index) => (
          <React.Fragment key={index}>{button}</React.Fragment>
        ))}
      </ButtonsContainer>
    </Container>
  );
};

export default compose<CongratulationsDayProps, OuterCongratulationsDayProps>(
  withRouter,
  withSendSeriesEndOfDayReminder,
  withSeriesTrackingIds,
)(CongratulationsDay);
